import { api } from '@/services/api/AxiosBaseService'

/** Uma rotina do agendador (lida do próprio Schedule do backend). */
export interface ScheduledRoutine {
  command: string
  expression: string
  description: string | null
  nextRunAt: string | null
  isBackup: boolean
}

/** Um dump guardado no bucket. */
export interface BackupFile {
  path: string
  name: string
  size: number
  createdAt: string | null
}

export type BackupRunKind = 'BACKUP' | 'RESTORE'
export type BackupRunStatus = 'PENDENTE' | 'PROCESSANDO' | 'CONCLUIDO' | 'FALHOU'

/** Uma execução de backup ou restauração. */
export interface BackupRun {
  id: string
  kind: BackupRunKind
  trigger: 'AGENDADO' | 'MANUAL'
  status: BackupRunStatus
  filePath: string | null
  fileName: string | null
  fileSize: number | null
  safetyFilePath: string | null
  userName: string | null
  startedAt: string | null
  finishedAt: string | null
  durationSeconds: number | null
  error: string | null
  createdAt: string | null
}

export interface BackupsOverview {
  schedules: ScheduledRoutine[]
  files: BackupFile[]
  runs: BackupRun[]
  busy: { id: string; kind: BackupRunKind; status: BackupRunStatus } | null
  storage: { prefix: string; bucket: string | null; count: number; totalSize: number; keep: number }
}

/** Frase que o backend exige para liberar a restauração. */
export const RESTORE_CONFIRMATION = 'RESTAURAR'

export const BackupsService = {
  async overview(): Promise<BackupsOverview> {
    const r = await api.get<{ data: BackupsOverview }>('/integrations/backups')
    return r.data.data
  },

  /** Dispara um backup agora. O dump roda em job — acompanhe por `runStatus`. */
  async run(): Promise<BackupRun> {
    const r = await api.post<{ data: BackupRun }>('/integrations/backups')
    return r.data.data
  },

  /**
   * Restaura o banco a partir de um dump. Exige a frase de confirmação: é a
   * ação mais destrutiva do painel.
   */
  async restore(filePath: string, confirmation: string): Promise<BackupRun> {
    const r = await api.post<{ data: BackupRun }>('/integrations/backups/restore', {
      file_path: filePath,
      confirmation,
    })
    return r.data.data
  },

  async runStatus(id: string): Promise<BackupRun> {
    const r = await api.get<{ data: BackupRun }>(`/integrations/backups/runs/${id}`)
    return r.data.data
  },

  /** Sobe um dump trazido de fora para a pasta de backups. */
  async upload(file: File): Promise<{ path: string }> {
    const fd = new FormData()
    fd.append('file', file)
    const r = await api.post<{ data: { path: string } }>('/integrations/backups/upload', fd)
    return r.data.data
  },

  async downloadUrl(filePath: string): Promise<string | null> {
    const r = await api.get<{ data: { url: string | null } }>('/integrations/backups/download', {
      params: { file_path: filePath },
    })
    return r.data.data.url
  },

  async destroy(filePath: string): Promise<void> {
    await api.delete('/integrations/backups', { params: { file_path: filePath } })
  },
}

/** "1,4 GB" — tamanhos de dump não cabem em KB. */
export function humanSize(bytes: number | null | undefined): string {
  if (!bytes) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let v = bytes
  let i = 0
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${units[i]}`
}

/** "2 min 13 s" — duração de um dump. */
export function humanDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return `${seconds} s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s === 0 ? `${m} min` : `${m} min ${s} s`
}

/**
 * Traduz a expressão cron das rotinas do sistema.
 *
 * Cobre só os formatos que o agendador usa (diário, a cada N minutos, dia fixo
 * do mês); qualquer outro cai na própria expressão, que é melhor que uma
 * tradução errada.
 */
export function humanCron(expression: string): string {
  const [min, hour, day, , weekday] = expression.split(' ')

  const at = (h: string, m: string) => `${h.padStart(2, '0')}:${m.padStart(2, '0')}`

  if (min?.startsWith('*/') && hour === '*') return `a cada ${min.slice(2)} minutos`
  if (hour?.startsWith('*/')) return `a cada ${hour.slice(2)} horas`
  if (day !== '*' && weekday === '*' && !hour?.includes('*')) {
    return `todo dia ${day} às ${at(hour, min)}`
  }
  if (day === '*' && weekday === '*' && !hour?.includes('*') && !min?.includes('*')) {
    return `todos os dias às ${at(hour, min)}`
  }
  return expression
}
